import { useLocation } from 'react-router-dom'
import { useQuery } from '../../../routes'
import { SearchIndexIdentifier, useAdminSchoolSearchQuery } from 'epc-schema'
import { humanize } from 'utils'
import { flat } from 'utils'
import { BaseQueryOptions } from '@apollo/client'
import { useEffect, useRef } from 'react'

export interface PaginatedSchoolsOptions {
  fetchPolicy?: BaseQueryOptions['fetchPolicy']
}

export const usePaginatedSchools = (options: PaginatedSchoolsOptions) => {
  const query = useQuery<{
    page?: number
    appliedFacets?: string[]
    queryString?: string
  }>()

  const { search: locationSearch } = useLocation()
  const toggleFacet = (facet: string, action: 'remove' | 'add') => {
    const valueToRemove = action === 'remove' ? facet : null
    const updatedFacets = flat([[appliedFacets], facet], 2).filter(
      (i: string) => i !== valueToRemove
    ) as string[]
    const uniqueFacets = updatedFacets.filter(
      (i, idx) => updatedFacets.indexOf(i) === idx
    )
    query.set({ ...query.search, appliedFacets: uniqueFacets })
  }

  const setQueryString = (queryString: string) =>
    query.set({ ...query.search, queryString, page: 1 })

  const { data, loading, error, refetch } = useAdminSchoolSearchQuery({
    fetchPolicy: options?.fetchPolicy ?? 'network-only',
    variables: {
      queryString: query.search.queryString ?? '',
      facetFilters: query.search.appliedFacets ?? [],
      page: (query.search.page ?? 1) - 1,
      pageSize: 12,
    },
  })

  const updatingIndex = (data?.updatingIndicies ?? []).find(
    (u) => u?.indexId === SearchIndexIdentifier.Schools
  )
  const currentPage = (data?.search?.schools?.searchInfo?.page ?? 0) + 1
  const pageCount = data?.search?.schools?.searchInfo?.pageCount ?? 0
  const schools = data?.search?.schools?.nodes
  const success = data?.search?.success
  const facets = JSON.parse(data?.search?.schools?.searchInfo?.facets || '{}')
  const queryString = query.search.queryString
  const appliedFacets = query.search.appliedFacets ?? []
  const handlePageClick = ({ historyPush }: { historyPush?: boolean }) => (
    page: number
  ) => {
    query.set({ ...query.search, page }, historyPush)
  }

  const interval = useRef<number>()
  useEffect(() => {
    if (typeof updatingIndex?.progress === 'undefined') {
      clearInterval(interval.current)
      return
    }
    if (!interval.current) {
      interval.current = setInterval(async () => {
        console.log('Refetching:', updatingIndex?.progress)
        await refetch()
      }, 10000)
    }
  }, [updatingIndex, interval, refetch])

  const humanizedAppliedFacets = flat([appliedFacets], 2)
    .map((f: string) => f.split(':'))
    .map((f: string[]) => ({
      id: f.join(':'),
      name: humanize(f[0]),
      value: humanize(f[1]),
    }))

  return {
    updatingIndex: updatingIndex ? true : false,
    percentUpdated: updatingIndex?.progress,
    appliedFacets,
    currentPage,
    pageCount,
    error,
    facets,
    refetch,
    loading,
    schools,
    queryString,
    setQueryString,
    search: query.search,
    handlePageClick,
    success,
    toggleFacet,
    locationSearch,
    humanizedAppliedFacets,
  }
}
